import { useCallback, useState } from 'react';
import { GRID_CELL_MIME, type CellDragPayload, parseCellDragPayload } from './gridCellDnd';

const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function pickFirstImage(files: FileList | null): File | null {
    if (!files) return null;
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ACCEPTED_IMAGE_TYPES.has(file.type)) return file;
    }
    return null;
}

function isFileDrag(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer.types).includes('Files');
}

function isCellDrag(e: React.DragEvent): boolean {
    return Array.from(e.dataTransfer.types).includes(GRID_CELL_MIME);
}

interface UseCellDragAndDropParams {
    cellIndex: number;
    onDragStartCell: (cellIndex: number) => string | null;
    onMoveCell: (targetCellIndex: number, payload: CellDragPayload) => void;
    onDropFile: (cellIndex: number, file: File) => void;
}

/**
 * Drag-and-drop for a single grid cell. Two drag sources are accepted: OS
 * image files (copied in) and an existing photo dragged from another
 * cell/grid (moved/swapped via the GRID_CELL_MIME payload). Other drags
 * (text, whole-grid Tiptap drags) are ignored so the editor's normal drag
 * behaviors aren't hijacked.
 */
export function useCellDragAndDrop({
    cellIndex,
    onDragStartCell,
    onMoveCell,
    onDropFile
}: UseCellDragAndDropParams) {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleCellDragStart = useCallback(
        (e: React.DragEvent) => {
            const payload = onDragStartCell(cellIndex);
            if (!payload) return; // empty cell — nothing to move
            e.stopPropagation(); // keep ProseMirror from dragging the whole grid
            e.dataTransfer.setData(GRID_CELL_MIME, payload);
            e.dataTransfer.effectAllowed = 'move';
        },
        [cellIndex, onDragStartCell]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        if (isCellDrag(e)) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            setIsDragOver(true);
            return;
        }
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        if (!isFileDrag(e) && !isCellDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        if (!isFileDrag(e) && !isCellDrag(e)) return;
        // relatedTarget is null/outside when leaving this element entirely.
        const next = e.relatedTarget as Node | null;
        if (next && (e.currentTarget as HTMLElement).contains(next)) return;
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (isCellDrag(e)) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragOver(false);
                const payload = parseCellDragPayload(e.dataTransfer.getData(GRID_CELL_MIME));
                if (payload) onMoveCell(cellIndex, payload);
                return;
            }
            if (!isFileDrag(e)) return;
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            const file = pickFirstImage(e.dataTransfer.files);
            if (file) onDropFile(cellIndex, file);
        },
        [cellIndex, onDropFile, onMoveCell]
    );

    return {
        isDragOver,
        handleCellDragStart,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDrop
    };
}
